import api from './api';
import {
  QuickStatsQuery,
  QuickStatsApiResponse,
  ReportsDataQuery,
  ReportsDataResponse,
  TrendsQuery,
  TrendsResponse,
} from '@/types/advertisers.types';

/**
 * Advertisers Service
 * Handles all advertiser-related API calls
 */
class AdvertisersService {
  private readonly baseUrl = '/v1/advertisers';

  /**
   * Get quick stats for dashboard
   * Fetches aggregated performance metrics (Revenue, Spend, P&L, ROAS)
   *
   * @param params Query parameters (optional fromDate, toDate)
   * @returns Promise with quick stats data
   */
  async getQuickStats(params?: QuickStatsQuery): Promise<QuickStatsApiResponse> {
    const response = await api.get<QuickStatsApiResponse>(
      `${this.baseUrl}/quick-stats`,
      { params }
    );
    return response.data;
  }

  /**
   * Get reports data for charts and tables
   * Fetches campaign/ad-level performance records
   *
   * @param params Query parameters (required fromDate, toDate)
   * @returns Promise with array of performance records
   */
  async getReportsData(params: ReportsDataQuery): Promise<ReportsDataResponse> {
    const response = await api.get<ReportsDataResponse>(
      '/v1/reports/data',
      { params }
    );
    return response.data;
  }

  /**
   * Get ads created trends
   * Fetches count of new ads created per day
   *
   * @param params Query parameters (optional fromDate, toDate, advertiser)
   * @returns Promise with trends data (array of date/count pairs)
   */
  async getAdsCreatedTrends(params?: TrendsQuery): Promise<TrendsResponse> {
    const response = await api.get<TrendsResponse>(
      `${this.baseUrl}/ads-created-trends`,
      { params }
    );
    return response.data;
  }

  /**
   * Get campaigns created trends
   * Fetches count of new campaigns created per day
   *
   * @param params Query parameters (optional fromDate, toDate, advertiser)
   * @returns Promise with trends data (array of date/count pairs)
   */
  async getCampaignsCreatedTrends(params?: TrendsQuery): Promise<TrendsResponse> {
    const response = await api.get<TrendsResponse>(
      `${this.baseUrl}/campaigns-created-trends`,
      { params }
    );
    return response.data;
  }
}

// Export singleton instance
export const advertisersService = new AdvertisersService();
export default advertisersService;
